"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, ArrowRight, Check, X, Globe } from "lucide-react";
import { Cairo } from "next/font/google";
import { ssoApi, ApiError, setTokens, TwoFactorRequiredResponse } from "@/lib/api";
import { TwoFactorStep } from "@/components/login/two-factor-step";
import { localeNames, type Locale } from "@/i18n/config";
import Cookies from "js-cookie";

const cairo = Cairo({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700", "800"],
  display: "swap",
});

// UAE Pass inline SVG icon
const UAEPassIcon = () => (
  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0865 2.10437C12.2304 1.80567 14.4141 2.15149 16.3637 3.09846C16.679 3.2639 17.0392 3.39127 17.2731 3.66944C17.4489 3.89637 17.3254 4.21846 17.1467 4.40147C16.9099 4.61229 16.6049 4.44247 16.3652 4.33266C14.7962 3.51718 13.0385 3.01501 11.2632 3.08236C9.83145 3.15244 8.4348 3.55317 7.1811 4.2536C6.98658 4.37274 6.77485 4.46065 6.55352 4.5142C6.07558 4.48492 5.85331 3.74997 6.3022 3.5084C7.45266 2.77895 8.74163 2.29973 10.0865 2.10144" fill="#1D1C1B"/>
    <path d="M11.0388 4.52615C11.2625 4.42659 11.5196 4.47783 11.7651 4.46466C13.7321 4.4793 15.7165 5.07078 17.2956 6.2713C18.1148 6.89329 18.8354 7.63724 19.4326 8.47763C19.484 8.55409 19.5155 8.64236 19.5242 8.73432C19.5329 8.82627 19.5185 8.91896 19.4823 9.00383C19.4462 9.08871 19.3894 9.16304 19.3172 9.21999C19.245 9.27694 19.1598 9.31467 19.0694 9.32971C18.7207 9.40145 18.5319 9.0574 18.343 8.82754C16.7145 6.70027 14.0444 5.42069 11.37 5.56709C10.7802 5.68568 10.4983 4.7765 11.0359 4.52615" fill="#1D1C1B"/>
    <path d="M8.96206 4.82317C9.05772 4.7751 9.16532 4.75653 9.2714 4.76979C9.37748 4.78305 9.47732 4.82755 9.55842 4.89773C9.63953 4.9679 9.69829 5.06062 9.72736 5.16429C9.75642 5.26795 9.7545 5.37796 9.72183 5.48053C9.60126 5.87289 9.13203 5.86704 8.81534 6.00027C7.10985 6.53757 5.73558 7.81716 4.74773 9.28414C4.57776 9.52571 4.40198 9.82291 4.08093 9.86976C3.67272 9.85073 3.33714 9.36174 3.59282 8.99133C4.31046 8.02505 5.07168 7.06317 6.05372 6.35603C6.93828 5.69837 7.91831 5.18231 8.95916 4.82609" fill="#1D1C1B"/>
    <path d="M4.62279 4.90448C4.95837 4.62045 5.52493 4.92937 5.47699 5.36566C5.4774 5.45743 5.45449 5.54779 5.41042 5.62811C5.36636 5.70843 5.30261 5.77604 5.22526 5.82448C5.14791 5.87292 5.05954 5.90059 4.96857 5.90485C4.8776 5.90911 4.78706 5.88981 4.70559 5.84879C4.6236 5.80413 4.55388 5.73967 4.50267 5.66119C4.45146 5.5827 4.42037 5.49265 4.41216 5.39908C4.40396 5.30552 4.4189 5.21136 4.45566 5.12504C4.49242 5.03873 4.54984 4.96294 4.62279 4.90448Z" fill="#1D1C1B"/>
    <path d="M10.7032 6.83922C11.6828 6.68248 12.6838 6.72819 13.6453 6.97359C14.6068 7.21899 15.5087 7.65894 16.2962 8.26668C17.0764 8.86807 17.7167 9.63415 18.1722 10.5112C18.6276 11.3883 18.8873 12.3552 18.9329 13.344C18.9619 14.3454 18.9154 15.4113 18.4084 16.3014C18.1984 16.6568 17.8903 16.9429 17.5217 17.1249C17.1531 17.3069 16.7399 17.3768 16.3325 17.3262C15.8516 17.2457 15.7514 16.5166 16.1785 16.3014C16.5068 16.1784 16.8903 16.2589 17.1954 16.0642C17.7562 15.7465 17.945 15.057 17.99 14.4538C18.0845 12.9429 17.6995 11.3646 16.7625 10.1626C15.9734 9.16958 14.8828 8.46494 13.658 8.15687C11.9656 7.70887 10.0698 7.82014 8.54302 8.73371C6.82155 9.72488 5.70877 11.6076 5.46762 13.5739C5.2962 14.7861 5.46762 16.0174 5.79157 17.1886C5.87583 17.4346 5.87147 17.7479 5.6463 17.9133C5.57671 17.9689 5.49435 18.006 5.40681 18.021C5.31927 18.0361 5.22938 18.0287 5.14543 17.9995C5.06148 17.9703 4.98617 17.9203 4.92645 17.8541C4.86673 17.7879 4.82452 17.7075 4.80373 17.6205C4.22264 15.5796 4.14129 13.3235 4.949 11.3251C5.87728 8.91965 8.21035 7.24623 10.7032 6.83922Z" fill="#1D1C1B"/>
    <path d="M12.0439 9.27505C12.1165 9.22661 12.1997 9.19678 12.2864 9.18816C12.3731 9.17953 12.4605 9.19237 12.5411 9.22556C12.6217 9.25875 12.6931 9.31129 12.749 9.3786C12.8048 9.44591 12.8436 9.52596 12.8617 9.61178C12.9809 10.0217 12.5233 10.4448 12.1354 10.2545C12.0456 10.2127 11.9684 10.1478 11.9114 10.0663C11.8544 9.98489 11.8197 9.88977 11.8107 9.79054C11.8018 9.6913 11.8188 9.59143 11.8603 9.50095C11.9017 9.41046 11.966 9.33256 12.0468 9.27505" fill="#1D1C1B"/>
    <path d="M14.026 10.0084C14.12 9.94251 14.2329 9.90954 14.3473 9.91456C14.4617 9.91959 14.5714 9.96233 14.6593 10.0362C15.7967 10.9433 16.5434 12.2574 16.744 13.7051C16.7803 14.0346 16.8297 14.383 16.7164 14.7022C16.4898 15.1194 15.7402 14.973 15.7561 14.4635C15.6469 13.1061 15.0225 11.8433 14.0129 10.9381C13.9401 10.8843 13.881 10.8138 13.8407 10.7325C13.8004 10.6511 13.78 10.5612 13.7811 10.4702C13.7822 10.3793 13.8049 10.2899 13.8473 10.2096C13.8896 10.1293 13.9504 10.0603 14.0245 10.0084" fill="#1D1C1B"/>
    <path d="M19.8442 10.1535C20.174 9.91484 20.6723 10.2003 20.6621 10.6029C20.7115 11.0304 20.1682 11.3525 19.8268 11.0861C19.7531 11.0324 19.6934 10.9615 19.6528 10.8795C19.6123 10.7975 19.592 10.7068 19.5937 10.6152C19.5954 10.5236 19.6191 10.4337 19.6627 10.3533C19.7063 10.2729 19.7686 10.2044 19.8442 10.1535Z" fill="#1D1C1B"/>
    <path d="M2.75187 10.748C2.81457 10.7137 2.88352 10.6926 2.95454 10.6859C3.02557 10.6792 3.09721 10.6871 3.16511 10.7092C3.23302 10.7312 3.29578 10.7669 3.3496 10.8141C3.40342 10.8613 3.44718 10.919 3.47823 10.9837C3.62351 11.2267 3.47823 11.4903 3.4085 11.7304C3.20749 12.3758 3.08839 13.0444 3.05404 13.72C3.04515 13.7981 3.01903 13.8733 2.9776 13.9399C2.93616 14.0065 2.88046 14.0629 2.81458 14.105C2.74869 14.1472 2.67429 14.1739 2.59683 14.1833C2.51937 14.1926 2.44081 14.1844 2.3669 14.1592C2.25159 14.1201 2.1528 14.0429 2.0866 13.94C2.02039 13.8371 1.99067 13.7147 2.00227 13.5926C2.03658 12.8001 2.17582 12.0158 2.4163 11.2604C2.48312 11.0672 2.54995 10.8432 2.74897 10.748" fill="#1D1C1B"/>
    <path d="M10.8185 11.6767C11.1091 11.5991 11.4374 11.4878 11.7396 11.5845C11.8281 11.6249 11.9045 11.6882 11.961 11.7679C12.0176 11.8475 12.0523 11.9408 12.0617 12.0383C12.0712 12.1358 12.0549 12.2341 12.0147 12.3233C11.9745 12.4125 11.9116 12.4894 11.8325 12.5463C11.5289 12.6927 11.1672 12.6547 10.8723 12.826C10.2738 13.1188 9.94256 13.8069 9.9411 14.4628C9.95854 16.0542 10.4989 17.6837 11.6234 18.8271C12.3683 19.5973 13.2897 20.171 14.3065 20.4976C14.5971 20.5737 14.8876 20.849 14.7728 21.1799C14.7307 21.4976 14.3864 21.6425 14.1031 21.5561C12.6272 21.275 11.3807 20.2809 10.4713 19.1126C9.39198 17.6969 8.69612 15.8917 8.89224 14.0894C8.94805 13.5433 9.15928 13.025 9.50051 12.5969C9.84174 12.1689 10.2984 11.8493 10.8156 11.6767" fill="#1D1C1B"/>
    <path d="M20.7086 11.8959C20.7941 11.8638 20.8863 11.8539 20.9766 11.8669C21.0669 11.8799 21.1526 11.9155 21.2257 11.9705C21.2989 12.0255 21.3572 12.0981 21.3954 12.1816C21.4335 12.2651 21.4503 12.357 21.4442 12.4488C21.4381 12.5405 21.4092 12.6293 21.3603 12.7069C21.3114 12.7845 21.244 12.8486 21.1642 12.8932C21.0844 12.9378 20.9948 12.9616 20.9035 12.9623C20.8123 12.963 20.7223 12.9407 20.6418 12.8973C20.2568 12.6909 20.2961 12.0438 20.7086 11.8959Z" fill="#1D1C1B"/>
    <path d="M13.1567 12.3746C13.5126 12.1491 13.883 12.4669 14.0094 12.8021C14.1867 13.1654 14.3096 13.5532 14.374 13.9529C14.4089 14.4785 13.6477 14.7522 13.3571 14.3086C13.1814 13.9207 13.0971 13.4961 12.9344 13.1022C12.8706 12.9755 12.8575 12.8289 12.8977 12.6927C12.938 12.5565 13.0286 12.4411 13.1509 12.3702" fill="#1D1C1B"/>
    <path d="M11.424 14.012C11.7146 13.8217 12.1736 13.9725 12.1838 14.3488C12.2535 15.0808 12.3523 15.8275 12.7184 16.4775C13.0438 17.0921 13.5299 17.6052 14.124 17.9613C14.7181 18.3175 15.3975 18.503 16.0887 18.4979C16.3865 18.5199 16.7628 18.391 16.985 18.6604C17.0479 18.7325 17.0913 18.8196 17.1112 18.9134C17.1311 19.0073 17.1268 19.1047 17.0987 19.1963C17.0706 19.288 17.0196 19.3709 16.9506 19.437C16.8816 19.5031 16.7969 19.5503 16.7046 19.574C14.8423 19.8668 12.8942 18.8698 11.9252 17.2637C11.4528 16.4952 11.1939 15.6128 11.1756 14.7089C11.1756 14.463 11.1974 14.1599 11.424 14.012Z" fill="#1D1C1B"/>
    <path d="M14.3752 15.4757C14.7849 15.2854 15.2469 15.7788 15.0711 16.1872C15.0438 16.2697 14.9974 16.3443 14.9356 16.405C14.8739 16.4657 14.7986 16.5105 14.7162 16.5359C14.6337 16.5613 14.5464 16.5664 14.4616 16.5508C14.3767 16.5352 14.2968 16.4994 14.2285 16.4464C13.8988 16.1975 13.9801 15.6031 14.3738 15.4757" fill="#1D1C1B"/>
    <path d="M18.2385 17.7496C18.6104 17.4656 19.1712 17.8799 19.0549 18.3177C19.0402 18.4075 19.0032 18.4922 18.9474 18.5638C18.8916 18.6355 18.8188 18.6918 18.7356 18.7275C18.6525 18.7633 18.5618 18.7773 18.4718 18.7684C18.3819 18.7595 18.2957 18.7278 18.2211 18.6764C18.1486 18.6225 18.09 18.5519 18.0501 18.4705C18.0103 18.3891 17.9904 18.2992 17.9921 18.2084C17.9938 18.1177 18.0171 18.0287 18.0599 17.9488C18.1028 17.869 18.164 17.8007 18.2385 17.7496Z" fill="#1D1C1B"/>
    <path d="M5.73461 18.9919C5.81583 18.9442 5.90796 18.9188 6.00193 18.918C6.09589 18.9172 6.18844 18.9411 6.27043 18.9874C6.35242 19.0337 6.42102 19.1007 6.46947 19.1818C6.51791 19.263 6.54452 19.3554 6.54668 19.4501C6.5859 19.8674 6.07454 20.1895 5.72734 19.9435C5.64951 19.891 5.58584 19.8199 5.54203 19.7365C5.49823 19.6531 5.47568 19.5601 5.4764 19.4657C5.47712 19.3714 5.50109 19.2787 5.54616 19.1959C5.59123 19.1132 5.65599 19.0431 5.73461 18.9919Z" fill="#1D1C1B"/>
    <path d="M10.08 9.42967C10.3008 9.35501 10.5855 9.28327 10.7715 9.47067C11.0867 9.72102 10.9879 10.3037 10.5899 10.4062C9.99513 10.5929 9.44886 10.9102 8.99042 11.3353C8.53198 11.7603 8.17278 12.2826 7.93868 12.8643C7.58277 13.7267 7.59874 14.6871 7.7077 15.5992C7.84683 16.669 8.19229 17.701 8.7246 18.6371C8.97737 19.0031 8.60112 19.5507 8.17547 19.4482C7.74983 19.3457 7.66121 18.8479 7.47817 18.5156C6.8012 16.8026 6.28404 14.8847 6.77651 13.0517C7.1978 11.3944 8.45875 9.96405 10.0873 9.42967" fill="#1EAB75"/>
    <path d="M9.08217 20.1021C9.14036 20.0592 9.20639 20.0284 9.27644 20.0118C9.34649 19.9951 9.41912 19.993 9.49006 20.0054C9.56099 20.0179 9.62877 20.0447 9.68943 20.0841C9.7501 20.1235 9.80237 20.1748 9.84325 20.2349C10.4649 21.0118 11.3575 21.5356 12.3433 21.7003C12.4369 21.7192 12.5236 21.7624 12.595 21.8256C12.6663 21.8888 12.7198 21.9697 12.7503 22.0602C12.7807 22.1506 12.7871 22.2474 12.7687 22.3411C12.7503 22.4348 12.7078 22.5221 12.6453 22.5942C12.5828 22.6662 12.5024 22.7205 12.4122 22.7519C12.3219 22.7832 12.225 22.7905 12.1309 22.7731C11.0074 22.6103 9.97116 22.0843 9.18108 21.2756C9.12289 21.2184 9.07748 21.1494 9.04778 21.0732C9.01808 20.997 9.00479 20.9154 9.00875 20.8336C9.01271 20.7519 9.03383 20.672 9.07064 20.5992C9.10745 20.5264 9.15911 20.4623 9.22207 20.4109" fill="#1D1C1B"/>
  </svg>
);

type View = "login" | "forgot-email" | "forgot-otp" | "forgot-password" | "forgot-success";

function PasswordRequirements({ password, tf }: { password: string; tf: (key: string) => string }) {
  const rules = [
    { label: tf("ruleMinLength"), test: password.length >= 8 },
    { label: tf("ruleUppercase"), test: /[A-Z]/.test(password) },
    { label: tf("ruleLowercase"), test: /[a-z]/.test(password) },
    { label: tf("ruleNumber"), test: /[0-9]/.test(password) },
    { label: tf("ruleSpecial"), test: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) },
  ];

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 mt-1">
      <p className="text-xs font-medium text-muted-foreground mb-2">{tf("passwordRequirements")}</p>
      <ul className="space-y-1">
        {rules.map((rule, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            {rule.test ? (
              <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
            ) : (
              <X className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
            )}
            <span className={rule.test ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}>
              {rule.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface LoginFormData {
  email: string;
  password: string;
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectUri = searchParams.get("redirect_uri");
  const clientId = searchParams.get("client_id");
  const uaePassError = searchParams.get("uaepass_error");
  const locale = useLocale() as Locale;
  const t = useTranslations("auth");
  const tf = useTranslations("auth.forgotPasswordFlow");
  const tc = useTranslations("common");

  const targetLocale: Locale = locale === "ar" ? "en" : "ar";

  const handleLanguageSwitch = () => {
    Cookies.set("locale", targetLocale, { expires: 365, sameSite: "lax" });
    router.refresh();
  };

  // ─── View state ─────────────────────────────────────────────────────────────
  const [view, setView] = useState<View>("login");

  // ─── Shared state ───────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ─── 2FA state ──────────────────────────────────────────────────────────────
  const [twoFactorRequired, setTwoFactorRequired] = useState<TwoFactorRequiredResponse | null>(null);
  const [twoFactorError, setTwoFactorError] = useState("");

  // ─── Forgot password state ──────────────────────────────────────────────────
  const [fpEmail, setFpEmail] = useState("");
  const [fpEmailHint, setFpEmailHint] = useState("");
  const [fpResetToken, setFpResetToken] = useState("");
  const [fpResendsRemaining, setFpResendsRemaining] = useState(3);
  const [fpCooldown, setFpCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ─── Cooldown timer ─────────────────────────────────────────────────────────
  const startCooldown = useCallback((seconds: number) => {
    setFpCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setFpCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // ─── UAE Pass error from callback redirect ─────────────────────────────────
  useEffect(() => {
    if (uaePassError) {
      const errorMap: Record<string, string> = {
        // UAE Pass redirect errors (mapped by middleware from error + error_description)
        access_denied: t("errors.uaePassAccessDenied"),
        uaepass_cancelled_on_app: t("errors.uaePassCancelledOnApp"),
        uaepass_cancelled_on_web: t("errors.uaePassCancelledOnWeb"),
        uaepass_cancelled: t("errors.uaePassCancelled"),
        uaepass_session_expired: t("errors.uaePassSessionExpired"),
        uaepass_server_error: t("errors.uaePassServerError"),
        uaepass_invalid_request: t("errors.uaePassInvalidRequest"),
        // Backend validation errors
        invalid_state: t("errors.uaePassInvalidState"),
        uaepass_error: t("errors.uaePassError"),
        account_inactive: t("errors.uaePassAccountInactive"),
        missing_params: t("errors.uaePassMissingParams"),
        // SOP verification errors
        uaepass_sop_insufficient: t("errors.uaePassSopInsufficient"),
        uaepass_sop_digital_signature: t("errors.uaePassSopDigitalSignature"),
        uaepass_visitor_account: t("errors.uaePassVisitorAccount"),
        // Other errors
        user_matching_error: t("errors.uaePassUserMatchingError"),
        connection_error: t("errors.connectionError"),
        auth_error: t("errors.uaePassGenericError"),
      };
      setError(errorMap[uaePassError] || t("errors.uaePassGenericError"));
      // Clean up URL params without reload
      const url = new URL(window.location.href);
      url.searchParams.delete("uaepass_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, [uaePassError, t]);

  // ─── Login form schema & hook ───────────────────────────────────────────────
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("validation.emailRequired"))
          .email(t("validation.emailInvalid")),
        password: z.string().min(1, t("validation.passwordRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  // ─── Forgot password form schemas & hooks ───────────────────────────────────
  const fpEmailSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("validation.emailRequired"))
          .email(t("validation.emailInvalid")),
      }),
    [t]
  );

  const fpOtpSchema = useMemo(
    () => z.object({ code: z.string().regex(/^\d{6}$/, tf("invalidCode")) }),
    [tf]
  );

  const fpPasswordSchema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(8, tf("minLength"))
            .regex(/[A-Z]/, tf("requireUppercase"))
            .regex(/[a-z]/, tf("requireLowercase"))
            .regex(/[0-9]/, tf("requireNumber"))
            .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, tf("requireSpecial")),
          confirmPassword: z.string().min(1, tf("confirmRequired")),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: tf("mismatch"),
          path: ["confirmPassword"],
        }),
    [tf]
  );

  const fpEmailForm = useForm<{ email: string }>({
    resolver: zodResolver(fpEmailSchema),
    defaultValues: { email: "" },
  });

  const fpOtpForm = useForm<{ code: string }>({
    resolver: zodResolver(fpOtpSchema),
    defaultValues: { code: "" },
  });

  const fpPasswordForm = useForm<{
    password: string;
    confirmPassword: string;
  }>({
    resolver: zodResolver(fpPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  // ─── View helpers ───────────────────────────────────────────────────────────
  function goToForgotPassword() {
    setError("");
    setView("forgot-email");
  }

  function goBackToLogin() {
    setView("login");
    setError("");
    setFpEmail("");
    setFpEmailHint("");
    setFpResetToken("");
    setFpResendsRemaining(3);
    setFpCooldown(0);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    fpEmailForm.reset();
    fpOtpForm.reset();
    fpPasswordForm.reset();
  }

  // ─── Auth check ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = Cookies.get("sso_access_token");

      // Check if user has any valid token (access or refresh)
      const refreshToken = Cookies.get("sso_refresh_token");
      const hasAnyToken = token || refreshToken;

      if (hasAnyToken) {
        if (redirectUri && clientId) {
          // tryAuthorize internally calls ensureValidToken() which handles refresh
          handleOAuthRedirect(token || "");
        } else {
          // Use silentMe() instead of me() to avoid authFetch's aggressive
          // redirect-to-login behavior which clears all tokens on failure
          const user = await ssoApi.silentMe();
          if (user) {
            if (user.is_superuser) {
              router.replace("/dashboard");
            } else if (user.is_staff) {
              router.replace("/apps");
            } else {
              router.replace("/citizen");
            }
          } else {
            Cookies.remove("sso_access_token", { path: "/" });
            setCheckingAuth(false);
          }
        }
      } else {
        setCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, [redirectUri, clientId, router]);

  async function handleOAuthRedirect(_token: string) {
    try {
      // Use tryAuthorize which handles token refresh gracefully
      // without redirecting to /login on failure
      const result = await ssoApi.tryAuthorize({
        client_id: clientId!,
        redirect_uri: redirectUri!,
      });

      if (result) {
        window.location.href = `${result.redirect_uri}?code=${result.code}`;
      } else {
        Cookies.remove("sso_access_token", { path: "/" });
        setCheckingAuth(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const code = err.body?.error_code;
        if (code === "account_inactive") {
          // Genuine user-facing message: the account is disabled.
          setError((err.body?.error as string) || t("errors.accountDisabled"));
          Cookies.remove("sso_access_token", { path: "/" });
          setCheckingAuth(false);
          return;
        }
        // Any other access-denied code (legacy or new) → /apps banner.
        // Citizens, missing position_type, missing role — same UX.
        router.replace("/apps?error=no_system_access");
        return;
      }
      Cookies.remove("sso_access_token", { path: "/" });
      setCheckingAuth(false);
    }
  }

  // ─── Login handlers ─────────────────────────────────────────────────────────
  async function onSubmit(data: LoginFormData) {
    setError("");
    setLoading(true);

    try {
      const response = await ssoApi.login(data.email.trim(), data.password);

      if ("requires_2fa" in response && response.requires_2fa) {
        setTwoFactorRequired(response as unknown as TwoFactorRequiredResponse);
        setLoading(false);
        return;
      }

      await completeLogin(response);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 202 && err.body.requires_2fa) {
          setTwoFactorRequired(err.body as unknown as TwoFactorRequiredResponse);
          setLoading(false);
          return;
        }
        if (err.status === 429) {
          const retryAfter = err.body?.retry_after as number;
          if (retryAfter) {
            setError(t("errors.rateLimited", { seconds: retryAfter }));
          } else {
            setError((err.body?.error as string) || t("errors.rateLimitedGeneric"));
          }
        } else if (err.status === 401) {
          setError(t("errors.invalidCredentials"));
        } else if (err.status === 403) {
          setError(t("errors.accountDisabled"));
        } else if (err.status === 400) {
          // Show backend validation error if available
          const msg = (err.body?.error as string) || (err.body?.detail as string);
          setError(msg || t("errors.connectionError"));
        } else {
          setError(t("errors.connectionError"));
        }
      } else {
        setError(t("errors.connectionError"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function completeLogin(response: { access: string; refresh: string; access_lifetime?: number; user?: any; totp_setup_required?: boolean }) {
    setTokens(response.access, response.refresh, response.access_lifetime);

    if (response.user) {
      localStorage.setItem("sso_user", JSON.stringify(response.user));
    }

    // Force password change if required
    if (response.user?.must_change_password) {
      const changePasswordUrl = new URL("/auth/change-password", window.location.origin);
      if (clientId) changePasswordUrl.searchParams.set("client_id", clientId);
      if (redirectUri) changePasswordUrl.searchParams.set("redirect_uri", redirectUri);
      router.push(changePasswordUrl.pathname + changePasswordUrl.search);
      return;
    }

    // Force TOTP setup if required
    // (Demo/staging: backend TOTP_REQUIRED=False — skip even if flag accidentally set.)
    if (response.totp_setup_required && process.env.NEXT_PUBLIC_SSO_TOTP_FORCE !== "false") {
      const setupUrl = new URL("/setup-totp", window.location.origin);
      if (clientId) setupUrl.searchParams.set("client_id", clientId);
      if (redirectUri) setupUrl.searchParams.set("redirect_uri", redirectUri);
      router.push(setupUrl.pathname + setupUrl.search);
      return;
    }

    if (redirectUri && clientId) {
      const authorizeUrl = new URL(
        "/api/auth/authorize/",
        process.env.NEXT_PUBLIC_SSO_API_URL || "http://localhost:8001"
      );
      authorizeUrl.searchParams.set("client_id", clientId);
      authorizeUrl.searchParams.set("redirect_uri", redirectUri);

      const res = await fetch(authorizeUrl.toString(), {
        headers: { Authorization: `Bearer ${response.access}` },
      });

      if (!res.ok) {
        // Check if this is a system access denial.
        // We deliberately DO NOT surface the specific reason (citizen vs
        // position_type vs no_role) to the end user — those are admin-facing
        // diagnostics used in API responses and audit logs. From the user's
        // perspective, all "you can't access this system" cases collapse to
        // the same /apps page with a single banner. account_inactive is the
        // one exception: deactivated users genuinely need a direct message.
        try {
          const errorData = await res.json();
          if (res.status === 403) {
            const code = errorData.error_code;
            if (code === "account_inactive") {
              setError((errorData.error as string) || t("errors.accountDisabled"));
              setLoading(false);
              return;
            }
            // Everything else (no_system_access, no_role, citizen_blocked,
            // position_type_missing) → redirect to /apps so the user lands
            // somewhere usable. The /apps page surfaces a generic "you have
            // no access to this system" banner and lists whatever apps the
            // user CAN reach.
            router.replace("/apps?error=no_system_access");
            return;
          }
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error("Authorization failed");
      }

      const authorizeData = await res.json();
      window.location.href = `${authorizeData.redirect_uri}?code=${authorizeData.code}`;
    } else {
      const isSuperuser = response.user?.is_superuser ?? false;
      const isStaff = response.user?.is_staff ?? false;

      if (isSuperuser) {
        router.push("/dashboard");
      } else if (isStaff) {
        router.push("/apps");
      } else {
        router.push("/citizen");
      }
    }
  }

  // ─── 2FA handlers ──────────────────────────────────────────────────────────
  async function handleVerify2FA(code: string, method: string, trustDevice: boolean) {
    if (!twoFactorRequired) return;

    setTwoFactorError("");
    try {
      const response = await ssoApi.verify2FA({
        session_token: twoFactorRequired.session_token,
        code,
        method: method as "email_otp" | "totp" | "backup_code",
        trust_device: trustDevice,
      });
      await completeLogin(response);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          const retryAfter = err.body?.retry_after as number;
          if (retryAfter) {
            setTwoFactorError(t("errors.rateLimited", { seconds: retryAfter }));
          } else {
            setTwoFactorError((err.body?.error as string) || t("errors.rateLimitedGeneric"));
          }
        } else {
          setTwoFactorError(err.body?.error as string || "رمز التحقق غير صحيح");
        }
      } else {
        setTwoFactorError("حدث خطأ. يرجى المحاولة مرة أخرى.");
      }
    }
  }

  async function handleResendOTP() {
    if (!twoFactorRequired) return { resends_remaining: 0 };
    try {
      return await ssoApi.resend2FAOTP(twoFactorRequired.session_token);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const retryAfter = err.body?.retry_after as number;
        if (retryAfter) {
          setTwoFactorError(t("errors.rateLimited", { seconds: retryAfter }));
        } else {
          setTwoFactorError((err.body?.error as string) || t("errors.rateLimitedGeneric"));
        }
      } else if (err instanceof ApiError) {
        setTwoFactorError((err.body?.error as string) || t("errors.connectionError"));
      }
      return { resends_remaining: 0 };
    }
  }

  function handleCancel2FA() {
    setTwoFactorRequired(null);
    setTwoFactorError("");
  }

  async function handleUAEPassLogin() {
    setError("");
    setLoading(true);
    try {
      const params: { client_id?: string; redirect_uri?: string } = {};
      if (clientId) params.client_id = clientId;
      if (redirectUri) params.redirect_uri = redirectUri;
      const result = await ssoApi.uaePassAuthorize(params);
      sessionStorage.setItem("uaepass_state", result.state);
      window.location.href = result.authorization_url;
    } catch {
      setError(t("errors.connectionError"));
      setLoading(false);
    }
  }

  // ─── Forgot password handlers ───────────────────────────────────────────────
  async function handleFpSendOTP(data: { email: string }) {
    setError("");
    setLoading(true);
    try {
      const res = await ssoApi.forgotPasswordSendOTP(data.email.trim());
      setFpEmail(data.email.trim());
      setFpEmailHint(res.email_hint);
      setFpResendsRemaining(res.resends_remaining);
      startCooldown(60);
      setView("forgot-otp");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          const retryAfter = err.body?.retry_after as number;
          setError(retryAfter ? t("errors.rateLimited", { seconds: retryAfter }) : ((err.body?.error as string) || t("errors.rateLimitedGeneric")));
        } else {
          setError((err.body?.error as string) || t("errors.connectionError"));
        }
      } else {
        setError(t("errors.connectionError"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleFpVerifyOTP(data: { code: string }) {
    setError("");
    setLoading(true);
    try {
      const res = await ssoApi.forgotPasswordVerifyOTP(fpEmail, data.code);
      setFpResetToken(res.reset_token);
      setView("forgot-password");
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body?.error as string) || t("errors.connectionError"));
      } else {
        setError(t("errors.connectionError"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleFpResend() {
    if (fpCooldown > 0 || fpResendsRemaining <= 0) return;
    setError("");
    try {
      const res = await ssoApi.forgotPasswordSendOTP(fpEmail);
      setFpResendsRemaining(res.resends_remaining);
      startCooldown(60);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          const retryAfter = err.body?.retry_after as number;
          setError(retryAfter ? t("errors.rateLimited", { seconds: retryAfter }) : ((err.body?.error as string) || t("errors.rateLimitedGeneric")));
        } else {
          setError((err.body?.error as string) || t("errors.connectionError"));
        }
      }
    }
  }

  async function handleFpResetPassword(data: { password: string; confirmPassword: string }) {
    setError("");
    setLoading(true);
    try {
      await ssoApi.forgotPasswordReset(fpResetToken, data.password, data.confirmPassword);
      setView("forgot-success");
      setTimeout(() => {
        goBackToLogin();
      }, 3000);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body?.error as string) || t("errors.connectionError"));
      } else {
        setError(t("errors.connectionError"));
      }
    } finally {
      setLoading(false);
    }
  }

  // ─── Error banner ──────────────────────────────────────────────────────────
  const errorBanner = error ? (
    <div className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <span>{error}</span>
    </div>
  ) : null;

  // ─── Early returns ──────────────────────────────────────────────────────────

  if (checkingAuth) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-foreground" />
          <p className="text-sm text-muted-foreground">{t("checking")}</p>
        </div>
      </div>
    );
  }

  if (twoFactorRequired) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 lg:px-20 min-h-screen">
        <TwoFactorStep
          sessionToken={twoFactorRequired.session_token}
          methods={twoFactorRequired.methods}
          emailHint={twoFactorRequired.email_hint}
          preferredMethod={twoFactorRequired.preferred_method}
          expiresIn={twoFactorRequired.expires_in}
          onVerify={handleVerify2FA}
          onResendOTP={handleResendOTP}
          onCancel={handleCancel2FA}
          error={twoFactorError}
        />
      </div>
    );
  }

  // ─── Derive header text from view ───────────────────────────────────────────
  const headerTitle =
    view === "login" ? t("loginTitle") :
    view === "forgot-email" ? tf("title") :
    view === "forgot-otp" ? tf("otpTitle") :
    view === "forgot-password" ? tf("newPasswordTitle") :
    "";

  const headerSubtitle =
    view === "login" ? t("loginSubtitle") :
    view === "forgot-email" ? tf("subtitle") :
    view === "forgot-otp" ? tf("otpSubtitle", { email: fpEmailHint }) :
    view === "forgot-password" ? tf("newPasswordSubtitle") :
    "";

  // ─── Main render ────────────────────────────────────────────────────────────

  return (
    <div className={`flex flex-1 flex-col items-center justify-center px-6 py-10 lg:px-20 min-h-screen ${locale === "en" ? cairo.className : ""}`}>
      <div className="w-full max-w-[400px] flex flex-col items-center">
        {/* Logo for mobile */}
        <div className="lg:hidden mb-8">
          <img
            src="/ffaa-mark.png"
            alt={t("loginTitle")}
            className="h-24 w-auto dark:brightness-110"
          />
        </div>

        {/* ── Success view (no header, just card) ────────────────────── */}
        {view === "forgot-success" ? (
          <div className="w-full bg-card border border-border rounded-2xl p-8 shadow-[0px_10px_15px_0px_rgba(0,0,0,0.1),0px_4px_6px_0px_rgba(0,0,0,0.1)] dark:shadow-[0px_10px_15px_0px_rgba(0,0,0,0.3),0px_4px_6px_0px_rgba(0,0,0,0.2)]">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                {tf("successTitle")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {tf("successMessage")}
              </p>
              <p className="text-xs text-muted-foreground">
                {tf("redirecting")}
              </p>
              <button
                type="button"
                onClick={goBackToLogin}
                className="text-sm text-primary hover:underline mt-2"
              >
                {tf("backToLogin")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Page Header */}
            <div className="text-center w-full mb-10">
              <h1 className="text-[30px] font-bold text-foreground mb-2 leading-[1.32]">
                {headerTitle}
              </h1>
              <p className="text-sm text-muted-foreground leading-[1.4]">
                {headerSubtitle}
              </p>
            </div>

            {/* Card */}
            <div className="w-full bg-card border border-border rounded-2xl p-6 shadow-[0px_10px_15px_0px_rgba(0,0,0,0.1),0px_4px_6px_0px_rgba(0,0,0,0.1)] dark:shadow-[0px_10px_15px_0px_rgba(0,0,0,0.3),0px_4px_6px_0px_rgba(0,0,0,0.2)]">

              {/* Language Switcher */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={handleLanguageSwitch}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span>{localeNames[targetLocale]}</span>
                </button>
              </div>

              {/* ── Login form ─────────────────────────────────────────── */}
              {view === "login" && (
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
                  {errorBanner}

                  {/* Email Field */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm text-foreground text-start">
                      {t("email")}
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...register("email")}
                      placeholder={t("emailPlaceholder")}
                      autoComplete="email"
                      autoFocus
                      disabled={loading}
                      className={`w-full h-9 rounded-lg border bg-input px-3 text-sm text-start text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-border"
                      }`}
                    />
                    {errors.email && (
                      <p className="text-xs text-red-600 dark:text-red-400 text-start">{errors.email.message}</p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label htmlFor="password" className="text-sm text-foreground">
                        {t("password")}
                      </label>
                      <button
                        type="button"
                        onClick={goToForgotPassword}
                        className="text-xs text-foreground hover:underline"
                      >
                        {t("forgotPassword")}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        placeholder={t("passwordPlaceholder")}
                        autoComplete="current-password"
                        disabled={loading}
                        className={`w-full h-9 rounded-lg border bg-input px-3 pe-10 text-sm text-start text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${
                          errors.password ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-border"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-red-600 dark:text-red-400 text-start">{errors.password.message}</p>
                    )}
                  </div>

                  {/* Button Group */}
                  <div className="flex flex-col gap-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary h-9 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{t("loggingIn")}</span>
                        </>
                      ) : (
                        <span>{t("login")}</span>
                      )}
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-sm text-muted-foreground">{t("orDivider")}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    {/* UAE Pass Button */}
                    <button
                      type="button"
                      onClick={handleUAEPassLogin}
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-outline-border bg-card h-9 text-sm font-bold text-foreground transition-all hover:bg-accent hover:border-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UAEPassIcon />
                      <span>{t("uaePassLogin")}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* ── Forgot: Email step ─────────────────────────────────── */}
              {view === "forgot-email" && (
                <form onSubmit={fpEmailForm.handleSubmit(handleFpSendOTP)} className="flex flex-col gap-6">
                  {errorBanner}

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fp-email" className="text-sm text-foreground text-start">
                      {tf("emailLabel")}
                    </label>
                    <input
                      id="fp-email"
                      type="email"
                      {...fpEmailForm.register("email")}
                      placeholder={tf("emailPlaceholder")}
                      autoComplete="email"
                      autoFocus
                      disabled={loading}
                      className={`w-full h-9 rounded-lg border bg-input px-3 text-sm text-start text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${
                        fpEmailForm.formState.errors.email
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-border"
                      }`}
                    />
                    {fpEmailForm.formState.errors.email && (
                      <p className="text-xs text-red-600 dark:text-red-400 text-start">
                        {fpEmailForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary h-9 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{tf("sending")}</span>
                      </>
                    ) : (
                      <span>{tf("sendCode")}</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                    {tf("backToLogin")}
                  </button>
                </form>
              )}

              {/* ── Forgot: OTP step ───────────────────────────────────── */}
              {view === "forgot-otp" && (
                <form onSubmit={fpOtpForm.handleSubmit(handleFpVerifyOTP)} className="flex flex-col gap-6">
                  {errorBanner}

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fp-code" className="text-sm text-foreground text-start">
                      {tf("otpLabel")}
                    </label>
                    <input
                      id="fp-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      {...fpOtpForm.register("code")}
                      placeholder={tf("otpPlaceholder")}
                      autoComplete="one-time-code"
                      autoFocus
                      disabled={loading}
                      className={`w-full h-9 rounded-lg border bg-input px-3 text-sm text-center text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 tracking-[0.5em] font-mono ${
                        fpOtpForm.formState.errors.code
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-border"
                      }`}
                    />
                    {fpOtpForm.formState.errors.code && (
                      <p className="text-xs text-red-600 dark:text-red-400 text-start">
                        {fpOtpForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary h-9 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{tf("verifying")}</span>
                      </>
                    ) : (
                      <span>{tf("verifyCode")}</span>
                    )}
                  </button>

                  {/* Resend */}
                  <div className="text-center">
                    {fpResendsRemaining > 0 ? (
                      <button
                        type="button"
                        onClick={handleFpResend}
                        disabled={fpCooldown > 0}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {fpCooldown > 0
                          ? tf("resendIn", { seconds: fpCooldown })
                          : tf("resendCode")}
                      </button>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {tf("resendExhausted")}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                    {tf("backToLogin")}
                  </button>
                </form>
              )}

              {/* ── Forgot: Password step ──────────────────────────────── */}
              {view === "forgot-password" && (
                <form onSubmit={fpPasswordForm.handleSubmit(handleFpResetPassword)} className="flex flex-col gap-6">
                  {errorBanner}

                  {/* New Password */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fp-password" className="text-sm text-foreground text-start">
                      {tf("newPassword")}
                    </label>
                    <div className="relative">
                      <input
                        id="fp-password"
                        type={showNewPassword ? "text" : "password"}
                        {...fpPasswordForm.register("password")}
                        placeholder={tf("newPasswordPlaceholder")}
                        autoFocus
                        disabled={loading}
                        className={`w-full h-9 rounded-lg border bg-input px-3 pe-10 text-sm text-start text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${
                          fpPasswordForm.formState.errors.password
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-border"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        tabIndex={-1}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>

                    {/* Password Requirements Checklist */}
                    <PasswordRequirements password={fpPasswordForm.watch("password")} tf={tf} />
                  </div>

                  {/* Confirm Password */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fp-confirm-password" className="text-sm text-foreground text-start">
                      {tf("confirmPassword")}
                    </label>
                    <div className="relative">
                      <input
                        id="fp-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        {...fpPasswordForm.register("confirmPassword")}
                        placeholder={tf("confirmPasswordPlaceholder")}
                        disabled={loading}
                        className={`w-full h-9 rounded-lg border bg-input px-3 pe-10 text-sm text-start text-foreground outline-none transition-all placeholder:text-muted-foreground shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${
                          fpPasswordForm.formState.errors.confirmPassword
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-border"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {fpPasswordForm.formState.errors.confirmPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 text-start">
                        {fpPasswordForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary h-9 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{tf("resetting")}</span>
                      </>
                    ) : (
                      <span>{tf("resetPassword")}</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                    {tf("backToLogin")}
                  </button>
                </form>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-10 space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("footerText")}
          </p>
          <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
            <span>{tc("allRightsReserved")}</span>
            <span>&copy;</span>
            <span>{tc("copyrightYear")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
